// client/src/components/PracticeScreen.js
//
// Team tab — /teams/:teamId/practice. Practices from Schedule (type=
// Practice) with their PracticeBlocks as nested drill agenda.
//
// Layout: hero (next practice, blocks always shown) + upcoming list +
// recent list. Non-hero practices show block count only; tap to expand
// the full agenda.
//
// Block times are NOT stored — computed from parent practice's
// scheduled_at + cumulative sequence×duration. If a practice gets
// rescheduled, block times shift automatically.
//
// v1 limitations, mirroring ScheduleScreen:
//   - No home_away, no scores, no practice focus/title field (yet)
//   - Status Upcoming/Cancelled/Postponed apply to practices;
//     Final/Tournament apply to games only and are ignored here
// Search "SCHEMA-TODO" when related columns land.

import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch';
import { PageTitle } from './shell/PageTitle';
import { Scrawl } from './mischief/Scrawl';
import { Sticker } from './mischief/Sticker';

// ── Date/time formatting (same as ScheduleScreen) ───────────────────────
// If this pattern comes up a third time, promote to src/lib/timeFormat.js.
function toDate(iso) {
  if (!iso) return null;
  const normalized = iso.includes('T') ? iso : iso.replace(' ', 'T');
  return new Date(normalized);
}

function formatEventDate(iso) {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  }).toUpperCase().replace(',', '');
}

function formatEventTime(iso) {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit',
  });
}

function formatDayNumber(iso) {
  const d = toDate(iso);
  if (!d) return '';
  return String(d.getDate());
}

function formatMonthAbbr(iso) {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
}

// ── Block time computation ──────────────────────────────────────────────
// Block start time = practice scheduled_at + sum of prior block durations.
// Returned as a formatted "H:MM AM/PM" string. If practice has no start
// time, returns null and the UI skips the time column.
function blockStartTime(practiceIso, blocksBefore) {
  const base = toDate(practiceIso);
  if (!base) return null;
  const minutesOffset = blocksBefore.reduce(
    (sum, b) => sum + (b.duration_minutes ?? 0),
    0,
  );
  const t = new Date(base.getTime() + minutesOffset * 60_000);
  return t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// Total duration of a practice = sum of block durations. Useful for the
// collapsed row's "1h 15m" caption.
function totalDuration(blocks) {
  const mins = blocks.reduce((sum, b) => sum + (b.duration_minutes ?? 0), 0);
  if (mins <= 0) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// ── partitionByTime ─────────────────────────────────────────────────────
// Same shape as ScheduleScreen — hero (next upcoming) + upcoming +
// recent. Endpoint returns DESC; we reverse for ASC upcoming.
function partitionByTime(events) {
  const now = Date.now();
  const upcoming = [];
  const recent = [];

  for (const e of events) {
    const t = toDate(e.scheduled_at)?.getTime();
    if (t == null) continue;
    if (t >= now) upcoming.push(e);
    else recent.push(e);
  }

  upcoming.reverse(); // soonest first
  const [hero, ...restUpcoming] = upcoming;
  return { hero: hero ?? null, upcoming: restUpcoming, recent };
}

// ── BlockList ───────────────────────────────────────────────────────────
// Renders a practice's drill agenda. Time · Title (block sequence order)
// with optional notes below and duration on the right.
function BlockList({ practice }) {
  const blocks = practice.blocks ?? [];
  if (blocks.length === 0) {
    return (
      <div className="mt-4 text-[12px] text-inkSub italic">
        No agenda yet — coach hasn't planned this one.
      </div>
    );
  }

  return (
    <div className="mt-4">
      {blocks.map((b, i) => {
        const priorBlocks = blocks.slice(0, i);
        const startAt = blockStartTime(practice.scheduled_at, priorBlocks);

        return (
          <div
            key={b.id}
            className="py-2.5 border-t border-rule grid grid-cols-[60px_1fr_auto] gap-3 items-start"
          >
            <div className="text-[11px] font-semibold text-inkSub pt-0.5">
              {startAt}
            </div>
            <div>
              <div className="text-[14px] font-semibold text-ink">
                {b.title}
              </div>
              {b.notes && (
                <div className="text-[12px] text-inkSub mt-1 whitespace-pre-wrap">
                  {b.notes}
                </div>
              )}
            </div>
            <div className="text-[11px] text-inkSub tabular-nums pt-0.5">
              {b.duration_minutes}m
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── HeroCard ────────────────────────────────────────────────────────────
// Next practice — always-shown blocks.
function HeroCard({ practice }) {
  const cancelled = practice.status === 'Cancelled' || practice.status === 'Postponed';

  return (
    <article
      className={`
        relative rounded-2xl bg-paper border border-rule
        px-5 py-5
        ${cancelled ? 'opacity-60' : ''}
      `}
    >
      <div className="text-[10px] tracking-[0.16em] font-bold uppercase text-inkSub">
        {formatEventDate(practice.scheduled_at)} · {formatEventTime(practice.scheduled_at)}
      </div>

      <h2 className="font-serif text-[26px] leading-tight text-ink mt-1">
        Practice
      </h2>

      <div className="text-[12px] text-inkSub mt-2">
        {practice.location_name}
        {practice.location_city && (
          <span> · {practice.location_city}{practice.location_state && `, ${practice.location_state}`}</span>
        )}
      </div>

      {cancelled && (
        <Sticker tone="default" className="absolute -top-2 right-4">
          {practice.status?.toUpperCase()}
        </Sticker>
      )}

      <BlockList practice={practice} />
    </article>
  );
}

// ── ListRow ─────────────────────────────────────────────────────────────
// Collapsed practice row: day/month tile + title + duration total + chevron.
// Tap to expand — reveals the BlockList inline underneath.
function ListRow({ practice }) {
  const [expanded, setExpanded] = useState(false);
  const cancelled = practice.status === 'Cancelled' || practice.status === 'Postponed';
  const dur = totalDuration(practice.blocks ?? []);
  const blockCount = (practice.blocks ?? []).length;

  return (
    <div className={cancelled ? 'opacity-60' : ''}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full py-3.5 border-t border-rule grid grid-cols-[54px_1fr_auto] gap-3.5 items-center text-left"
      >
        <div>
          <div className="font-serif text-[26px] leading-none">
            {formatDayNumber(practice.scheduled_at)}
          </div>
          <div className="text-[9px] tracking-[0.1em] font-bold uppercase text-inkSub mt-0.5">
            {formatMonthAbbr(practice.scheduled_at)}
          </div>
        </div>

        <div>
          <div className="text-[14px] font-semibold text-ink">
            Practice
          </div>
          <div className="text-[11px] text-inkSub mt-0.5">
            {[
              formatEventTime(practice.scheduled_at),
              practice.location_name,
              blockCount > 0 && `${blockCount} ${blockCount === 1 ? 'block' : 'blocks'}`,
              dur,
            ].filter(Boolean).join(' · ')}
          </div>
        </div>

        <div>
          {cancelled ? (
            <Sticker tone="default">{practice.status?.toUpperCase()}</Sticker>
          ) : (
            <span className="text-inkSub text-[16px]">
              {expanded ? '▾' : '▸'}
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="pl-[68px] pr-2 pb-3">
          <BlockList practice={practice} />
        </div>
      )}
    </div>
  );
}

// ── SectionLabel ────────────────────────────────────────────────────────
function SectionLabel({ children, count }) {
  return (
    <div className="mt-8 mb-2 flex items-baseline justify-between">
      <div className="text-[10px] tracking-[0.16em] font-bold uppercase text-inkSub">
        {children}
      </div>
      {count != null && (
        <div className="text-[11px] text-inkSub">
          {count} {count === 1 ? 'practice' : 'practices'}
        </div>
      )}
    </div>
  );
}

// ── PracticeScreen ──────────────────────────────────────────────────────
export function PracticeScreen() {
  const { team } = useOutletContext();
  const { data, loading, error } = useFetch(
    team?.id ? `/teams/${team.id}/practices` : null,
  );

  if (loading) {
    return (
      <div className="px-5 pt-6 pb-8">
        <PageTitle eyebrow={team?.name?.toUpperCase() ?? 'TEAM'}>
          Practice
        </PageTitle>
        <div className="mt-8">
          <div className="rounded-2xl bg-paper border border-rule px-5 py-5 animate-pulse">
            <div className="h-3 bg-rule rounded w-1/3" />
            <div className="mt-3 h-8 bg-rule rounded w-2/3" />
            <div className="mt-3 h-3 bg-rule rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-5 pt-6 pb-8">
        <PageTitle eyebrow={team?.name?.toUpperCase() ?? 'TEAM'}>
          Practice
        </PageTitle>
        <div className="mt-6 rounded-2xl bg-paper border border-rule px-5 py-4">
          <h3 className="font-serif text-[20px] text-ink">Something's off.</h3>
          <Scrawl className="mt-2" color="inkSub">
            couldn't load practices — try refresh?
          </Scrawl>
        </div>
      </div>
    );
  }

  const { hero, upcoming, recent } = partitionByTime(data ?? []);
  const hasAnything = hero || upcoming.length > 0 || recent.length > 0;

  return (
    <div className="px-5 pt-6 pb-8">
      <PageTitle eyebrow={team?.name?.toUpperCase() ?? 'TEAM'}>
        Practice
      </PageTitle>

      {!hasAnything && (
        <div className="text-center pt-10 pb-4">
          <div className="font-serif text-[32px] text-ink leading-tight">
            No practices scheduled
          </div>
          <Scrawl className="mt-2 inline-block" color="inkSub">
            check back when a coach plans one 🏒
          </Scrawl>
        </div>
      )}

      {hero && (
        <div className="mt-6">
          <HeroCard practice={hero} />
        </div>
      )}

      {upcoming.length > 0 && (
        <>
          <SectionLabel count={upcoming.length}>Upcoming</SectionLabel>
          <div>
            {upcoming.map(p => <ListRow key={p.id} practice={p} />)}
          </div>
        </>
      )}

      {recent.length > 0 && (
        <>
          <SectionLabel count={recent.length}>Recent</SectionLabel>
          <div>
            {recent.map(p => <ListRow key={p.id} practice={p} />)}
          </div>
        </>
      )}
    </div>
  );
}